import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { DashboardStats } from '../types';
import { formatNumber, formatCurrency, parseDate } from '../lib/utils';
import { simpleApiService } from '../services/simpleApi';
import { apiService } from '../services/api';
// import { useColors } from '../contexts/ColorContext';
import UserInfo from '../components/UserInfo';
import QuickRenewModal from '../components/modals/QuickRenewModal';
import RenewalSuccessModal from '../components/modals/RenewalSuccessModal';
import TrialCreatedModal from '../components/modals/TrialCreatedModal';
import { useToastNotifications } from '../components/ui/Toast';
import { useConfirmModal } from '../components/ui/ConfirmModal';
import { useAuth } from '../hooks/useAuth';
import { useQuickTrialHttp } from '../hooks/useQuickTrialHttp';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/Select';
import { Label } from '../components/ui/Label';
//import { Input } from '../components/ui/Input';
import Pagination from '../components/ui/Pagination';
import { 
  Users, 
  UserCheck, 
  Shield, 
  Activity,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Eye,
  Clock,
  Download,
  RefreshCw,
  AlertTriangle,
  Smartphone,
  Tv,
  Play,
  Plus,
  Megaphone,
  Info,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Lock,
  BarChart3,
  Trophy, // eslint-disable-line @typescript-eslint/no-unused-vars -- usado no Top 10 (condicional isAdmin)
  //Copy,
  //CheckCircle,
  Zap
} from 'lucide-react';

// Componente de gráfico de receita simples
const RevenueChart: React.FC<{ data: any[] }> = ({ data }) => {
  const safeData = Array.isArray(data) ? data : [];
  if (safeData.length === 0) return null;

  const chartHeight = 200;
  const chartWidth = 1000;
  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const maxRevenue = Math.max(...safeData.map(d => d.revenue || 0), 1);
  const minRevenue = Math.min(...safeData.map(d => d.revenue || 0), 0);

  // Normalizar dados para o gráfico
  const normalizedData = safeData.map((item, index) => {
    const x = (index / (safeData.length - 1 || 1)) * innerWidth;
    const y = innerHeight - ((item.revenue - minRevenue) / (maxRevenue - minRevenue || 1)) * innerHeight;
    return { ...item, x: x + padding.left, y: y + padding.top };
  });

  // Criar path para a linha
  const pathData = normalizedData.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  return (
    <div className="w-full overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="w-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        {/* Linha de fundo */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + innerHeight}
          stroke="#E5E7EB"
          strokeWidth="2"
        />
        <line
          x1={padding.left}
          y1={padding.top + innerHeight}
          x2={padding.left + innerWidth}
          y2={padding.top + innerHeight}
          stroke="#E5E7EB"
          strokeWidth="2"
        />

        {/* Grade horizontal */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = padding.top + innerHeight - (ratio * innerHeight);
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + innerWidth}
                y2={y}
                stroke="#F3F4F6"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="10"
                fill="#6B7280"
              >
                {formatCurrency(Math.round(maxRevenue * ratio))}
              </text>
            </g>
          );
        })}

        {/* Linha do gráfico */}
        <path
          d={pathData}
          fill="none"
          stroke="#10B981"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Área preenchida */}
        <path
          d={`${pathData} L ${normalizedData[normalizedData.length - 1].x} ${padding.top + innerHeight} L ${padding.left} ${padding.top + innerHeight} Z`}
          fill="url(#gradient)"
          opacity="0.2"
        />

        {/* Gradiente */}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Pontos e labels */}
        {normalizedData.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#10B981"
              stroke="white"
              strokeWidth="2"
            />
            <text
              x={point.x}
              y={padding.top + innerHeight + 20}
              textAnchor="middle"
              fontSize="10"
              fill="#6B7280"
              transform={`rotate(-45 ${point.x} ${padding.top + innerHeight + 20})`}
            >
              {point.month}
            </text>
            <title>
              {point.month}/{point.year}: {formatCurrency(point.revenue)}
            </title>
          </g>
        ))}
      </svg>
    </div>
  );
};

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : []);

/*
const normalizeTrialPayload = (payload: any) => {
  if (!payload || typeof payload !== 'object') return null;

  const username = String(payload.username || '').trim();
  const password = String(payload.password || '').trim();
  if (!username || !password) return null;

  return {
    username,
    password,
    expDate: payload.expDate || payload.exp_date || undefined,
    duration: payload.duration != null && Number.isFinite(Number(payload.duration))
      ? Number(payload.duration)
      : undefined,
  };
};
*/

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [revenuePerUser, setRevenuePerUser] = useState<number>(20); // valor padrão
  const [expiringClients, setExpiringClients] = useState<any[]>([]);
  const [appDownloads, setAppDownloads] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [isLoadingExpiring, setIsLoadingExpiring] = useState(false);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [revenueHistory, setRevenueHistory] = useState<any[]>([]);
  const [isLoadingRevenueHistory, setIsLoadingRevenueHistory] = useState(false);
  const [includeTrials, setIncludeTrials] = useState(false);
  const [expiringClientsPage, setExpiringClientsPage] = useState(1);
  const [expiringClientsPagination, setExpiringClientsPagination] = useState<any>(null);
  const [isRenewing, setIsRenewing] = useState<string | null>(null);
  const [showAllAnnouncements, setShowAllAnnouncements] = useState(false);
  const [selectedClientForRenew, setSelectedClientForRenew] = useState<any | null>(null);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewalSuccessData, setRenewalSuccessData] = useState<any | null>(null);
  const [isRenewalSuccessModalOpen, setIsRenewalSuccessModalOpen] = useState(false);
  const [p2pResyncJob, setP2pResyncJob] = useState<any | null>(null);
  const [isStartingP2pResync, setIsStartingP2pResync] = useState(false);
  /* eslint-disable @typescript-eslint/no-unused-vars -- topResellers* usados no Top 10 (condicional isAdmin) */
  const [topResellersActivations, setTopResellersActivations] = useState<Array<{ id: number; username: string; activationsCount: number }>>([]);
  const [isLoadingTopResellers, setIsLoadingTopResellers] = useState(false);
  const [topResellersPeriod, setTopResellersPeriod] = useState<{ month: number; year: number } | null>(null);
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const [topResellersPeriodFilter, setTopResellersPeriodFilter] = useState<string>(() => {
    const d = new Date();
    const prev = new Date(d.getFullYear(), d.getMonth() - 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });
  const [topResellersMode, setTopResellersMode] = useState<'all' | 'active_only'>('all');
  const [topResellersIncludeAdmins, setTopResellersIncludeAdmins] = useState(false);
  // const { colors } = useColors();

  const { isAdmin } = useAuth();
  const isAdminRef = React.useRef(isAdmin);
  isAdminRef.current = isAdmin;

  // Hooks para notificações modernas
  const { showSuccess, showError } = useToastNotifications();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const { generatingTrial, lastGeneratedTrial, setLastGeneratedTrial, handleGenerateTestAutomations } =
    useQuickTrialHttp();

  const handleResyncAllP2p = async () => {
    await showConfirm(
      'Sincronizar todos os P2P',
      'Isso irá resincronizar todos os clientes P2P (pode levar alguns minutos). Deseja iniciar agora?',
      {
        type: 'warning',
        confirmText: 'Iniciar',
        onConfirm: async () => {
          try {
            setIsStartingP2pResync(true);
            const response = await apiService.resyncAllP2pUsers({ includeMissing: false, delayMs: 0 });
            setP2pResyncJob(response?.job || null);
            showSuccess('Sincronização iniciada', 'Job de resincronização P2P iniciado com sucesso.');
          } catch (err: any) {
            showError('Erro ao iniciar', err?.message || 'Não foi possível iniciar a sincronização P2P');
          } finally {
            setIsStartingP2pResync(false);
          }
        }
      }
    );
  };

  // Poll do status do job (se existir)
  useEffect(() => {
    if (!p2pResyncJob?.id) return;
    if (p2pResyncJob.status !== 'running') return;

    const interval = setInterval(async () => {
      try {
        const data = await apiService.getResyncAllP2pStatus(p2pResyncJob.id);
        if (data?.job) {
          setP2pResyncJob(data.job);
        }
      } catch (err) {
        // Se falhar (ex: restart do servidor/job perdido), parar o polling para não ficar batendo infinito
        clearInterval(interval);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [p2pResyncJob?.id, p2pResyncJob?.status]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardData, cmsUserData] = await Promise.all([
          simpleApiService.getDashboardStats(),
          simpleApiService.getCmsUserCurrent()
        ]);
        setStats(dashboardData as DashboardStats);
        const userRevenue = cmsUserData?.user?.settings?.revenue_per_user || 20;
        setRevenuePerUser(userRevenue);
      } catch (error) {
        // Erro silenciado para evitar vazamento de dados
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Função para buscar clientes próximos do vencimento com paginação
  const fetchExpiringClients = useCallback(async (page: number = 1) => {
    setIsLoadingExpiring(true);
    try {
      const response = await apiService.getExpiringClients(includeTrials, page, 10);
      setExpiringClients(asArray<any>(response?.data));
      setExpiringClientsPagination(response.pagination || null);
      setExpiringClientsPage(page);
    } catch (error) {
      // Erro silenciado para evitar vazamento de dados
    } finally {
      setIsLoadingExpiring(false);
    }
  }, [includeTrials]);

  // Função para abrir modal de renovação
  const handleQuickRenewClick = (client: any) => {
    setSelectedClientForRenew(client);
    setIsRenewModalOpen(true);
  };

  // Função para confirmar renovação rápida
  const handleConfirmQuickRenew = async () => {
    if (!selectedClientForRenew) return;

    setIsRenewing(selectedClientForRenew.id.toString());
    try {
      const response = await apiService.quickRenewClient(selectedClientForRenew.id.toString());
      
      if (response.success) {
        const isTrial = selectedClientForRenew.is_trial;
        const renewedLine = response?.data?.line || {};
        const passwordFromApi =
          response?.data?.password ||
          renewedLine?.password ||
          selectedClientForRenew?.password ||
          '';
        const clientForModal = {
          ...selectedClientForRenew,
          password: passwordFromApi
        };
        
        // Mostrar toast de sucesso
        showSuccess(
          isTrial ? 'Teste Renovado' : 'Cliente Renovado',
          `Novo vencimento: ${response.data.new_exp_date_formatted}\nCusto: ${response.data.cost_paid} crédito(s)`
        );
        
        // Atualizar a lista de clientes próximos do vencimento
        await fetchExpiringClients(expiringClientsPage);
        
        // Fechar modal de renovação
        setIsRenewModalOpen(false);
        
        // Preparar dados para modal de sucesso
        setRenewalSuccessData({
          client: clientForModal,
          renewalData: response.data
        });
        
        // Abrir modal de sucesso com informações formatadas
        setIsRenewalSuccessModalOpen(true);
        
        // Limpar cliente selecionado
        setSelectedClientForRenew(null);
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.required && errorData?.available !== undefined) {
        showError(
          'Créditos Insuficientes',
          `Necessário: ${errorData.required} créditos\nDisponível: ${errorData.available} créditos`
        );
      } else {
        showError(
          'Erro na Renovação',
          errorData?.error || 'Erro ao renovar cliente'
        );
      }
    } finally {
      setIsRenewing(null);
    }
  };

  // Função para buscar aplicativos disponíveis
  const fetchAppDownloads = useCallback(async () => {
    setIsLoadingApps(true);
    try {
      const response = await simpleApiService.getAppDownloads();
      
      // Garantir que sempre seja um array válido
      // A resposta pode vir como { success: true, data: [...] } ou diretamente como array
      let apps: any[] = [];
      
      if (response?.success && Array.isArray(response.data)) {
        apps = response.data;
      } else if (Array.isArray(response.data)) {
        apps = response.data;
      } else if (Array.isArray(response)) {
        apps = response;
      }
      
      // Função para extrair ID de forma segura (formato MongoDB Extended JSON)
      const safeExtractId = (id: any): string => {
        if (!id) return '';
        if (typeof id === 'string') return id;
        if (typeof id === 'object' && id.$oid) return String(id.$oid);
        if (typeof id === 'object' && id._id) return safeExtractId(id._id);
        if (typeof id === 'object' && id.id) return safeExtractId(id.id);
        if (typeof id === 'object' && typeof id.toString === 'function') {
          const strValue = id.toString();
          if (strValue && strValue !== '[object Object]' && !strValue.includes('[object')) {
            return strValue;
          }
        }
        try {
          const jsonStr = JSON.stringify(id);
          const match = jsonStr.match(/"?\$oid"?\s*:\s*"([^"]+)"/);
          if (match) return match[1];
        } catch (e) {}
        return String(id);
      };
      
      // Processar apps: converter IDs e filtrar apenas válidos
      const processedApps = apps.map((app: any) => {
        const appId = safeExtractId(app._id || app.id);
        const processedVersions = Array.isArray(app.versions) 
          ? app.versions.map((v: any) => ({
              ...v,
              _id: safeExtractId(v._id)
            }))
          : [];
        
        return {
          ...app,
          _id: appId,
          versions: processedVersions,
          platforms: Array.isArray(app.platforms) ? app.platforms : []
        };
      });
      
      // Filtrar apenas apps válidos com pelo menos uma versão ativa
      const validApps = processedApps.filter((app: any) => {
        const hasValidId = app && app._id && app._id !== '[object Object]';
        const hasVersions = app && app.versions && Array.isArray(app.versions) && app.versions.length > 0;
        const hasActiveVersion = hasVersions && app.versions.some((v: any) => v.is_active === true);
        
        return hasValidId && hasActiveVersion;
      });
      
      setAppDownloads(validApps);
    } catch (error: any) {
      // Garantir array vazio em caso de erro
      setAppDownloads([]);
    } finally {
      setIsLoadingApps(false);
    }
  }, []);

  // Função para buscar anúncios
  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await apiService.getAnnouncements(10);
      
      // Verificar diferentes estruturas de resposta
      let announcementsData = [];
      if (Array.isArray(response)) {
        announcementsData = response;
      } else if (response.data) {
        announcementsData = Array.isArray(response.data) ? response.data : [];
      } else if (response.announcements) {
        announcementsData = Array.isArray(response.announcements) ? response.announcements : [];
      }
      
      setAnnouncements(announcementsData);
      
    } catch (error: any) {
      setAnnouncements([]);
    }
  }, []);

  // Carregar dados adicionais após o carregamento inicial
  // Função para buscar histórico de receita
  const fetchRevenueHistory = useCallback(async () => {
    setIsLoadingRevenueHistory(true);
    try {
      const response = await apiService.getRevenueHistory();
      setRevenueHistory(asArray<any>(response?.data));
    } catch (error) {
      // Erro silenciado para evitar vazamento de dados
    } finally {
      setIsLoadingRevenueHistory(false);
    }
  }, []);

  const fetchTopResellersActivations = useCallback(async () => {
    setIsLoadingTopResellers(true);
    try {
      const response = await apiService.getTopResellersActivations({
        period: topResellersPeriodFilter,
        mode: topResellersMode,
        include_admins: topResellersIncludeAdmins
      });
      if (response?.success && Array.isArray(response.data)) {
        setTopResellersActivations(response.data);
        setTopResellersPeriod(response.period || null);
      }
    } catch (error) {
      // Erro silenciado
    } finally {
      setIsLoadingTopResellers(false);
    }
  }, [topResellersPeriodFilter, topResellersMode, topResellersIncludeAdmins]);

  // Carregar dados secundários no mount (apenas uma vez, evita loop)
  useEffect(() => {
    fetchExpiringClients(1);
    fetchAppDownloads();
    fetchAnnouncements();
    fetchRevenueHistory();
    fetchTopResellersActivations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar expiring quando includeTrials mudar
  useEffect(() => {
    fetchExpiringClients(1);
  }, [includeTrials, fetchExpiringClients]);

  // Recarregar Top 10 quando filtros mudarem
  useEffect(() => {
    fetchTopResellersActivations();
  }, [fetchTopResellersActivations]);

  // Gerar opções de período (últimos 12 meses)
  const topResellersPeriodOptions = React.useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      opts.push({ value, label: `${monthNames[d.getMonth()]}/${d.getFullYear()}` });
    }
    return opts;
  }, []);

  // Preferir total online do escopo (usuário + cadeia). Fallback para somente do usuário
  const usersOnlineTotal = stats ? ((stats.users_online_of_resellers || 0) || (stats.users_online || 0)) : 0;

  const statCards = [
    {
      title: 'Total de Usuários',
      value: isLoading ? '—' : formatNumber(stats?.users || 0),
      icon: Users,
      color: '#3B82F6',
      bgColor: '#3B82F620',
    },
    {
      title: 'Usuários Ativos',
      value: isLoading ? '—' : formatNumber(stats?.users_active || 0),
      icon: Activity,
      color: '#10B981',
      bgColor: '#10B98120',
    },
    {
      title: 'Total de Revendedores',
      value: isLoading ? '—' : formatNumber(stats?.resellers || 0),
      icon: UserCheck,
      color: '#1E40AF',
      bgColor: '#1E40AF20',
    },
    {
      title: 'Online P2P',
      value: isLoading ? '—' : formatNumber(stats?.online_p2p ?? stats?.users_activep2p ?? 0),
      icon: Shield,
      color: '#F59E0B',
      bgColor: '#F59E0B20',
    },
    {
      title: 'Usuários Online',
      value: isLoading ? '—' : formatNumber(stats?.online_iptv ?? stats?.users_online ?? usersOnlineTotal),
      icon: Eye,
      color: '#3B82F6',
      bgColor: '#3B82F620',
    },
    {
      title: 'Conexões Ativas',
      value: isLoading ? '—' : formatNumber(stats?.conections_online ?? 0),
      icon: Zap,
      color: '#8B5CF6',
      bgColor: '#8B5CF620',
    },
    {
      title: 'Renda Total Média',
      value: isLoading ? '—' : formatCurrency((stats?.users_active ?? 0) * revenuePerUser),
      icon: DollarSign,
      color: '#10B981',
      bgColor: '#10B98120',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="page-header-mobile flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Dashboard
          </h1>
          <p className="text-sm text-gray-600 text-muted-foreground">
            Visão geral do sistema U2P
          </p>
        </div>

        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <Button
            variant="outline"
            onClick={handleResyncAllP2p}
            disabled={isStartingP2pResync || p2pResyncJob?.status === 'running'}
            className="flex items-center justify-center gap-2"
            title="Sincronizar todos os clientes P2P"
          >
            <RefreshCw className={`h-4 w-4 ${(isStartingP2pResync || p2pResyncJob?.status === 'running') ? 'animate-spin' : ''}`} />
            Sincronizar P2P
          </Button>
          {/* Botão do gerador de testes automáticos */}
          <Button
            variant="outline"
            onClick={handleGenerateTestAutomations}
            disabled={generatingTrial}
            className="flex items-center justify-center gap-2"
            title="Gerar teste rápido com credenciais"
          >
            {generatingTrial ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" />
                Teste Rápido HTTP
              </>
            )}
          </Button> 

          {p2pResyncJob && (
            <div className="text-xs text-gray-600 text-center sm:text-right">
              <div>
                Status: <span className="font-medium">{p2pResyncJob.status}</span>
                {typeof p2pResyncJob.processed === 'number' && typeof p2pResyncJob.total === 'number' && (
                  <span className="ml-2">
                    ({p2pResyncJob.processed}/{p2pResyncJob.total})
                  </span>
                )}
              </div>
              {p2pResyncJob.status !== 'running' && (
                <div className="text-[11px] text-gray-500">
                  OK: {p2pResyncJob.success || 0} | Falhas: {p2pResyncJob.failed || 0} | Pulados: {p2pResyncJob.skipped || 0}
                </div>
              )}
              {p2pResyncJob.status === 'failed' && p2pResyncJob.lastError && (
                <div className="text-[11px] text-red-600">
                  Último erro: {p2pResyncJob.lastError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal com credenciais e mensagem para copiar (teste rápido) */}
      <TrialCreatedModal
        isOpen={!!lastGeneratedTrial}
        onClose={() => setLastGeneratedTrial(null)}
        trial={
          lastGeneratedTrial
            ? {
                username: lastGeneratedTrial.username || '',
                password: lastGeneratedTrial.password || '',
                expDate: lastGeneratedTrial.expDate,
                duration: lastGeneratedTrial.duration
              }
            : { username: '', password: '' }
        }
      />

      {/* Informações do usuário logado */}
      <UserInfo />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="card-dynamic transition-colors">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle 
                className="text-sl font-large transition-colors"
                style={{ color: '#6B7280', opacity: 1 }}
              >
                {stat.title}
              </CardTitle>
              <div 
                className="p-2 rounded-full transition-colors"
                style={{ backgroundColor: stat.bgColor }}
              >
                <stat.icon 
                  className="h-8 w-8 transition-colors"
                  style={{ color: stat.color }}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className="text-2xl font-bold transition-colors"
                style={{ color: '#1F2937' }}
              >
                {stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-dynamic transition-colors">
          <CardHeader>
            <CardTitle 
              className="transition-colors"
              style={{ color: '#1F2937' }}
            >
              Receita Mensal
            </CardTitle>
            <CardDescription 
              className="transition-colors"
              style={{ color: '#6B7280', opacity: 1 }}
            >
              Receita total do mês atual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="text-3xl font-bold transition-colors"
              style={{ color: '#10B981' }}
            >
              {isLoading ? '—' : formatCurrency(stats?.current_month_revenue ?? ((stats?.users_active ?? 0) + (stats?.users_activep2p ?? 0)) * revenuePerUser)}
            </div>
            {stats?.revenue_percentage_change !== undefined && (
              <div className="flex items-center mt-2">
                {stats.revenue_percentage_change >= 0 ? (
                  <TrendingUp 
                    className="h-4 w-4 mr-1 transition-colors"
                    style={{ color: '#10B981' }}
                  />
                ) : (
                  <TrendingDown 
                    className="h-4 w-4 mr-1 transition-colors"
                    style={{ color: '#EF4444' }}
                  />
                )}
                <span 
                  className="text-sm transition-colors"
                  style={{ color: stats.revenue_percentage_change >= 0 ? '#10B981' : '#EF4444' }}
                >
                  {stats.revenue_percentage_change >= 0 ? '+' : ''}{stats.revenue_percentage_change.toFixed(2)}% em relação ao mês anterior
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 10 Revendedores - Admin: todo o sistema. Revendedor: sua árvore */}
        <Card className="card-dynamic transition-colors">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Top 10 Revendedores
              </CardTitle>
              <CardDescription>
                {topResellersPeriod
                  ? `Mais ativações em ${String(topResellersPeriod.month).padStart(2, '0')}/${topResellersPeriod.year}${isAdmin() ? '' : ' (sua árvore)'}`
                  : 'Clientes ativados'}
              </CardDescription>
              <p className="flex items-start gap-2 mt-2 text-xs text-muted-foreground">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Quem mais <strong>ativou clientes novos</strong> no período. O número mostra quantas novas linhas foram criadas no mês, não o total de clientes ativos de cada revendedor.</span>
              </p>
            </CardHeader>
            <CardContent>
              {/* Filtros */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-gray-600">Período</Label>
                  <Select value={topResellersPeriodFilter} onValueChange={setTopResellersPeriodFilter}>
                    <SelectTrigger className="h-9 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {topResellersPeriodOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-gray-600">Modo</Label>
                  <Select value={topResellersMode} onValueChange={(v) => setTopResellersMode(v as 'all' | 'active_only')}>
                    <SelectTrigger className="h-9 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as ativações</SelectItem>
                      <SelectItem value="active_only">Apenas ativos (não trial)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin() && (
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={topResellersIncludeAdmins}
                      onChange={(e) => setTopResellersIncludeAdmins(e.target.checked)}
                      className="rounded border-gray-300 text-amber-600"
                    />
                    <span className="text-sm text-gray-700">Incluir admins</span>
                  </label>
                </div>
                )}
              </div>
              {isLoadingTopResellers ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-amber-500" />
                  <span className="ml-2 text-muted-foreground">Carregando...</span>
                </div>
              ) : topResellersActivations.length > 0 ? (
                <ol className="space-y-2">
                  {topResellersActivations.map((item, index) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index < 3 ? 'bg-amber-500 text-white' : 'bg-gray-300 text-gray-700'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-900">{item.username}</span>
                      </div>
                      <span className="text-sm font-semibold text-green-600">
                        {formatNumber(item.activationsCount)} ativações
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  Nenhuma ativação no período
                </div>
              )}
            </CardContent>
          </Card>

        {/* Gráfico de Análise de Receita */}
        <Card className="card-dynamic transition-colors lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-500" />
              Análise de Receita (Últimos 12 Meses)
            </CardTitle>
            <CardDescription>
              Evolução mensal da receita baseada em clientes ativos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingRevenueHistory ? (
              <div className="flex items-center justify-center h-[200px]">
                <RefreshCw className="h-6 w-6 animate-spin text-green-500" />
                <span className="ml-2 text-muted-foreground">Carregando histórico...</span>
              </div>
            ) : revenueHistory.length > 0 ? (
              <RevenueChart data={revenueHistory} />
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seção de Avisos e Informações */}
        <Card className="card-dynamic transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-blue-500" />
              Avisos e Informações
            </CardTitle>
            <CardDescription className="flex items-center justify-between">
              <span>Comunicados importantes da administração</span>
              <button
                onClick={() => {
                  fetchAnnouncements();
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                Atualizar
              </button>
            </CardDescription>
          </CardHeader>
          <CardContent className="relative">
            {/* Container com altura máxima e scroll */}
            <div className={`transition-all duration-300 ${showAllAnnouncements ? 'max-h-[600px]' : 'max-h-[400px]'} overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100`}>
            {announcements.length > 0 ? (
              <div className="space-y-3">
                {(showAllAnnouncements ? announcements : announcements.slice(0, 3)).map((announcement, index) => {
                  // Verificar se é aviso estruturado ou texto simples
                  const isStructured = announcement.type && announcement.title;
                  
                  const getTypeIcon = () => {
                    switch (announcement.type) {
                      case 'info': return <Info className="h-4 w-4" />;
                      case 'warning': return <AlertCircle className="h-4 w-4" />;
                      case 'success': return <CheckCircle2 className="h-4 w-4" />;
                      case 'error': return <XCircle className="h-4 w-4" />;
                      default: return <Info className="h-4 w-4" />;
                    }
                  };

                  const getTypeColors = () => {
                    switch (announcement.type) {
                      case 'info': return 'bg-blue-50 border-blue-200 text-blue-800';
                      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
                      case 'success': return 'bg-green-50 border-green-200 text-green-800';
                      case 'error': return 'bg-red-50 border-red-200 text-red-800';
                      default: return 'bg-blue-50 border-blue-200 text-blue-800';
                    }
                  };

                  const getPriorityBadge = () => {
                    switch (announcement.priority) {
                      case 'urgent': return 'bg-red-100 text-red-800 font-bold';
                      case 'high': return 'bg-orange-100 text-orange-800 font-medium';
                      case 'medium': return 'bg-blue-100 text-blue-800';
                      case 'low': return 'bg-gray-100 text-gray-800';
                      default: return 'bg-gray-100 text-gray-600';
                    }
                  };
                  
                  const getPriorityLabel = () => {
                    switch (announcement.priority) {
                      case 'urgent': return '🔴 URGENTE';
                      case 'high': return '🟠 ALTA';
                      case 'medium': return '🔵 MÉDIA';
                      case 'low': return '⚪ BAIXA';
                      default: return 'INFO';
                    }
                  };

                  // Renderizar aviso estruturado (novo formato)
                  if (isStructured) {
                    return (
                      <div key={announcement._id || index} className={`p-4 rounded-lg border-l-4 ${getTypeColors()} shadow-sm hover:shadow-md transition-shadow`}>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getTypeIcon()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h4 className="font-semibold text-base">{announcement.title}</h4>
                              {announcement.priority && (
                                <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge()}`}>
                                  {getPriorityLabel()}
                                </span>
                              )}
                            </div>
                            <div className="text-sm opacity-90 whitespace-pre-wrap leading-relaxed">
                              {announcement.content}
                            </div>
                            <div className="flex items-center justify-end mt-3 pt-2 border-t border-current opacity-40">
                              <span className="text-xs">
                                📅 {(() => {
                                  const date = parseDate(announcement.createdAt);
                                  if (!date) return 'Data inválida';
                                  return date.toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  });
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Renderizar aviso legado (texto simples) com formatação melhorada
                  return (
                    <div key={announcement._id || index} className="p-4 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-white shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Info className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-800 leading-relaxed space-y-2">
                            {/* Dividir conteúdo em seções para melhor legibilidade */}
                            {announcement.content?.split('\n').map((line: string, i: number) => {
                              // Detectar títulos (linhas em CAPS ou com :)
                              const isTitle = line.trim().endsWith(':') || line === line.toUpperCase();
                              const isLink = line.includes('http') || line.includes('tinyurl');
                              
                              if (!line.trim()) return null;
                              
                              if (isTitle) {
                                return (
                                  <div key={i} className="font-bold text-blue-700 mt-3 first:mt-0">
                                    {line}
                                  </div>
                                );
                              }
                              
                              if (isLink) {
                                const linkMatch = line.match(/(https?:\/\/[^\s]+)/);
                                if (linkMatch) {
                                  return (
                                    <div key={i} className="flex items-center gap-2">
                                      <span>🔗</span>
                                      <a 
                                        href={linkMatch[1]} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline break-all"
                                      >
                                        {linkMatch[1]}
                                      </a>
                                    </div>
                                  );
                                }
                              }
                              
                              return (
                                <div key={i} className="pl-2">
                                  {line}
                                </div>
                              );
                            })}
                          </div>
                          {announcement.createdAt && (
                            <div className="flex items-center justify-end mt-3 pt-2 border-t border-blue-200 text-xs text-gray-600">
                              <span>
                                📅 {new Date(announcement.createdAt).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum aviso disponível no momento</p>
                <p className="text-sm text-gray-400 mt-2">
                  Os administradores podem criar avisos importantes aqui
                </p>
              </div>
            )}
            </div>
            
            {/* Botões de expandir/recolher - FORA do container com scroll */}
            {announcements.length > 3 && (
              <div className="text-center pt-3 mt-3 border-t border-gray-200">
                {!showAllAnnouncements ? (
                  <button 
                    onClick={() => setShowAllAnnouncements(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-2 mx-auto transition-colors"
                  >
                    <span>Ver todos os {announcements.length} avisos</span>
                    <span className="text-xs">↓</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => setShowAllAnnouncements(false)}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center justify-center gap-2 mx-auto transition-colors"
                  >
                    <span>Mostrar apenas os primeiros 3</span>
                    <span className="text-xs">↑</span>
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seção de Renovação Rápida */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-dynamic transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Renovação Rápida
            </CardTitle>
            <CardDescription>
              Clientes com expiração próxima (próximos 7 dias)
              {includeTrials && (
                <span className="block text-purple-600 text-sm mt-1">
                  ℹ️ Exibindo apenas linhas de teste (podem ser renovadas)
                </span>
              )}
            </CardDescription>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeTrials}
                  onChange={(e) => setIncludeTrials(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Mostrar apenas testes</span>
              </label>
              <button
                onClick={() => fetchExpiringClients(expiringClientsPage)}
                disabled={isLoadingExpiring}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${isLoadingExpiring ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingExpiring ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Carregando...</span>
              </div>
            ) : expiringClients.length > 0 ? (
              <div className="space-y-3">
                {expiringClients.map((client) => (
                  <div key={client.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                    client.is_trial 
                      ? 'bg-purple-50 border-purple-200' 
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{client.username}</span>
                        {client.is_trial && (
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 font-medium">
                            TESTE
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          client.days_until_exp === 0
                            ? 'bg-red-100 text-red-800' 
                            : client.days_until_exp === 1 
                            ? 'bg-red-100 text-red-800' 
                            : client.days_until_exp <= 3 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {client.days_until_exp === 0 
                            ? 'Hoje' 
                            : client.days_until_exp === 1 
                            ? 'Amanhã' 
                            : `${client.days_until_exp} dias`}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{client.email}</p>
                      <p className="text-xs text-gray-500">
                        {client.system} • {client.package_name}
                        {client.is_trial && (
                          <span className="ml-2 text-purple-600 font-medium">(Linha de Teste)</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleQuickRenewClick(client)}
                        disabled={isRenewing === client.id.toString()}
                        className={`p-2 rounded-lg transition-colors ${
                          isRenewing === client.id.toString()
                            ? 'text-blue-400 cursor-wait'
                            : client.is_trial
                            ? 'text-purple-600 hover:bg-purple-100'
                            : 'text-green-600 hover:bg-green-100'
                        }`}
                        title={client.is_trial ? 'Renovar teste por 1 mês' : 'Renovar por 1 mês'}
                      >
                        {isRenewing === client.id.toString() ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </button>
                      <span className="text-xs text-gray-500">
                        {client.is_trial ? (
                          <span className="text-purple-600 font-medium">Linha de Teste</span>
                        ) : (
                          `${client.max_connections} conexões`
                        )}
                      </span>
                    </div>
                  </div>
                ))}
                
                {/* Paginação */}
                {expiringClientsPagination && expiringClientsPagination.total_pages > 1 && (
                  <Pagination
                    currentPage={expiringClientsPagination.current_page}
                    totalPages={expiringClientsPagination.total_pages}
                    onPageChange={(page) => fetchExpiringClients(page)}
                    showInfo={true}
                    totalItems={expiringClientsPagination.total}
                    itemsPerPage={expiringClientsPagination.per_page}
                  />
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum cliente próximo do vencimento</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seção de Downloads de Aplicativos */}
        <Card className="card-dynamic transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-500" />
              Aplicativos para Download
            </CardTitle>
            <CardDescription>
              Players IPTV disponíveis para seus clientes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingApps ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Carregando...</span>
              </div>
            ) : appDownloads.length > 0 ? (
              <div className="space-y-3">
                {/* 🔒 CORREÇÃO: Filtrar apps inválidos e validar dentro do map */}
                {appDownloads.slice(0, 4).filter(app => app && app._id).map((app) => {
                  // Validação adicional: garantir que app existe
                  if (!app || !app._id) return null;
                  
                  // Pegar o link encurtado da primeira versão ativa
                  const activeVersion = app.versions?.find((v: any) => v.is_active);
                  const shortUrl = activeVersion?.short_url;
                  
                  const handleCopyLink = async () => {
                    if (!shortUrl) {
                      showError('Link não disponível para este aplicativo');
                      return;
                    }
                    
                    try {
                      // Tentar usar a API moderna do clipboard
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(shortUrl);
                        showSuccess('Link copiado para a área de transferência!');
                      } else {
                        // Fallback para navegadores mais antigos
                        const textArea = document.createElement('textarea');
                        textArea.value = shortUrl;
                        textArea.style.position = 'fixed';
                        textArea.style.opacity = '0';
                        textArea.style.left = '-999999px';
                        textArea.style.top = '-999999px';
                        document.body.appendChild(textArea);
                        textArea.focus();
                        textArea.select();
                        
                        try {
                          const successful = document.execCommand('copy');
                          // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                          try {
                            textArea.remove();
                          } catch (_) {
                            if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                          }
                          
                          if (successful) {
                            showSuccess('Link copiado para a área de transferência!');
                          } else {
                            throw new Error('Falha ao copiar usando execCommand');
                          }
                        } catch (fallbackErr) {
                          // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                          try {
                            textArea.remove();
                          } catch (_) {
                            if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                          }
                          // Último recurso: selecionar o texto para o usuário copiar manualmente
                          throw new Error('Não foi possível copiar automaticamente. Tente copiar manualmente.');
                        }
                      }
                    } catch (err: any) {
                      // Mostrar o link de forma destacada para o usuário copiar manualmente
                      showError(err.message || 'Erro ao copiar link. Tente copiar manualmente selecionando o texto.');
                    }
                  };
                  
                  const handleDownload = () => {
                    if (shortUrl) {
                      window.open(shortUrl, '_blank');
                    } else {
                      showError('Link não disponível');
                    }
                  };
                  
                  // Garantir que platforms é um array válido
                  const platforms = Array.isArray(app.platforms) ? app.platforms : [];
                  
                  return (
                    <div key={app._id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          {platforms.includes('android') && <Smartphone className="h-4 w-4 text-blue-600" />}
                          {(platforms.includes('samsung') || platforms.includes('lg')) && <Tv className="h-4 w-4 text-blue-600" />}
                          {platforms.includes('ios') && <Play className="h-4 w-4 text-blue-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900">{app.name}</h4>
                          <p className="text-sm text-gray-600">{app.description}</p>
                          <div className="flex gap-2 mt-1 items-center flex-wrap">
                            {/* 🔒 CORREÇÃO: Usar platforms ao invés de app.platforms */}
                            {platforms.map((platform: string) => (
                              <span key={platform} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {platform}
                              </span>
                            ))}
                            {activeVersion && activeVersion.version && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                v{activeVersion.version}
                              </span>
                            )}
                          </div>
                          
                          {/* Código de Acesso */}
                          {app.download_code && (
                            <div className="mt-3 p-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Lock className="h-4 w-4 text-green-600" />
                                  <span className="text-xs text-green-700 font-medium">Código de Acesso:</span>
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(app.download_code);
                                      showSuccess('Código copiado!');
                                    } catch (err) {
                                      showError('Erro ao copiar');
                                    }
                                  }}
                                  className="text-xs text-green-600 hover:text-green-800 font-medium hover:underline"
                                >
                                  📋 Copiar
                                </button>
                              </div>
                              <p className="text-lg font-mono font-bold text-green-900 tracking-wider text-center mt-1">
                                {app.download_code}
                              </p>
                              <p className="text-xs text-green-600 text-center mt-1">
                                Compartilhe este código com seus clientes
                              </p>
                            </div>
                          )}
                          
                          {shortUrl ? (
                            <>
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={handleCopyLink}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 hover:underline transition-colors"
                                  title="Copiar link de download"
                                >
                                  📋 Copiar link
                                </button>
                                <a
                                  href={shortUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1 hover:underline transition-colors"
                                  title="Abrir link de download em nova aba"
                                >
                                  🔗 Abrir link
                                </a>
                              </div>
                              <p 
                                className="text-xs text-gray-500 mt-1 font-mono break-all select-all cursor-text bg-gray-50 p-2 rounded border border-gray-200"
                                onClick={(e) => {
                                  // Selecionar todo o texto ao clicar
                                  const range = document.createRange();
                                  range.selectNodeContents(e.currentTarget);
                                  const selection = window.getSelection();
                                  selection?.removeAllRanges();
                                  selection?.addRange(range);
                                }}
                                title="Clique para selecionar o link"
                              >
                                {shortUrl}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                💡 Clique no link acima para selecioná-lo e copiar manualmente se necessário
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                              ⚠️ Link de download não disponível. Gere um link na página de gerenciamento de aplicativos.
                            </p>
                          )}
                        </div>
                      </div>
                      {shortUrl && (
                        <button 
                          onClick={handleDownload}
                          className="p-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm hover:shadow-md flex-shrink-0"
                          title="Baixar aplicativo"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Download className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum aplicativo disponível</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Renovação Rápida */}
      {selectedClientForRenew && (
        <QuickRenewModal
          isOpen={isRenewModalOpen}
          onClose={() => {
            setIsRenewModalOpen(false);
            setSelectedClientForRenew(null);
          }}
          onConfirm={handleConfirmQuickRenew}
          client={selectedClientForRenew}
          isLoading={isRenewing === selectedClientForRenew.id.toString()}
        />
      )}

      {/* Modal de Sucesso após Renovação */}
      {renewalSuccessData && (
        <RenewalSuccessModal
          isOpen={isRenewalSuccessModalOpen}
          onClose={() => {
            setIsRenewalSuccessModalOpen(false);
            setRenewalSuccessData(null);
          }}
          client={renewalSuccessData.client}
          renewalData={renewalSuccessData.renewalData}
        />
      )}

      {/* Modal de Confirmação Moderno */}
      <ConfirmModalComponent />
    </div>
  );
};

export default Dashboard;
